"use client";
import { api } from "@/utils/api";
import { atomWithStorage } from 'jotai/utils'
import { atom, useAtom } from "jotai";
import { type Session } from "@/types/auth";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";
import { type GetAllCustomersForUserOutput } from "@/types/types";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { userIsUniversal, userIsUniversalHeadAdmin } from "@/utils/accessControl";
import { CreateCustomerDialog } from "./CreateCustomerDialog";
import { cn } from "@/lib/utils";

// In-memory only: every page load starts unselected, so no query can fire
// with a customer this account has not been validated against.
export const selectedCustomerAtom = atom<string | undefined>(undefined);
export const activeCustomerAtom = atom<GetAllCustomersForUserOutput | undefined>(undefined);
// Persisted selection, keyed by user id: the same browser serves accounts
// with different memberships, and a selection leaking across accounts sends
// queries for a customer the account is not a member of, which the global
// 401 handler answers by signing the user out.
const storedSelectionsAtom = atomWithStorage<Record<string, string>>('selectedCustomerByUser', {});

export const CustomerSelector = ({ session }: { session: Session | null }) => {
    const [selectedCustomer, setSelectedCustomer] = useAtom(selectedCustomerAtom);
    const [activeCustomer, setActiveCustomer] = useAtom(activeCustomerAtom);
    const [storedSelections, setStoredSelections] = useAtom(storedSelectionsAtom);
    const [selectorDisabled, setSelectorDisabled] = useState(false);
    const [open, setOpen] = useState(false);
    const { data: customers } = api.customer.getAllForUser.useQuery(session?.user.id ?? '', { enabled: !!session });

    // Restore validates against session.customers, available at first render
    // (server-seeded), so scoped pages mount without waiting for the customers
    // query. Universal users may hold any customer; their reachable set is not
    // enumerated by their membership rows.
    const mayHold = (cuid: string) =>
        userIsUniversal(session) || session?.customers.some(uc => uc.customer_cuid === cuid) === true;

    // Once the customer list has arrived it is authoritative for everyone:
    // for universal users it is the only source that can veto a stored
    // selection naming a deleted customer. Before it arrives, unknown (not
    // false) keeps the session-based fast path in charge.
    const listedIn = (cuid: string) => customers?.some(c => c.id === cuid);

    useEffect(() => {
        if (!session) return;

        if (!selectedCustomer) {
            setActiveCustomer(undefined);
            const stored = storedSelections[session.user.id];
            if (stored && mayHold(stored) && listedIn(stored) !== false) {
                setSelectedCustomer(stored);
            } else if (!userIsUniversal(session) && session.customers.length === 1) {
                setSelectedCustomer(session.customers[0]?.customer_cuid);
            } else if (customers?.length === 1) {
                // Single-customer deployment: also auto-select for universal
                // users, whose memberships do not enumerate their reach.
                setSelectedCustomer(customers[0]?.id);
            }
            return;
        }

        if (!mayHold(selectedCustomer) || listedIn(selectedCustomer) === false) {
            // Membership changed or the customer no longer exists; fall back
            // to unselected and drop the stored entry so it cannot re-arm.
            setSelectedCustomer(undefined);
            setActiveCustomer(undefined);
            if (storedSelections[session.user.id] === selectedCustomer) {
                const rest = { ...storedSelections };
                delete rest[session.user.id];
                setStoredSelections(rest);
            }
            return;
        }

        if (storedSelections[session.user.id] !== selectedCustomer) {
            setStoredSelections({ ...storedSelections, [session.user.id]: selectedCustomer });
        }
        if (customers) {
            setActiveCustomer(customers.find(c => c.id === selectedCustomer));
            setSelectorDisabled(customers.length === 1 && !userIsUniversal(session));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mayHold derives from session
    }, [customers, selectedCustomer, storedSelections, setSelectedCustomer, setActiveCustomer, setStoredSelections, session]);

    if (!session) return null;
    
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button 
                    variant="outline" 
                    role="combobox"
                    aria-expanded={open}
                    className="h-12 text-foreground justify-between dark:border-border" 
                    disabled={selectorDisabled}
                >
                    <div className="grid flex-1 text-left text-sm leading-tight my-3">
                        {activeCustomer ?
                            <>
                                <span className="truncate font-semibold">
                                    {activeCustomer?.name}
                                </span>
                                <span className="truncate text-xs">{activeCustomer?.role}</span>
                            </>
                            : "Select a customer"
                        }
                    </div>
                    <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 mt-1 rounded-lg" align="end" side="bottom" sideOffset={4}>
                <Command filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                    <CommandInput className="focus:ring-0 focus:ring-offset-0 focus:outline-none border-none text-popover-foreground placeholder:text-muted-foreground" placeholder="Search customers..." />
                    <CommandList>
                        <CommandEmpty>No customer found.</CommandEmpty>
                        <CommandGroup heading="Customers">
                            {customers?.map((customer) => (
                                <CommandItem
                                    key={customer.id}
                                    value={customer.name}
                                    className="cursor-pointer"
                                    onSelect={() => {
                                        setSelectedCustomer(customer.id);
                                        setOpen(false);
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            "mr-2 h-4 w-4",
                                            selectedCustomer === customer.id ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    {customer.name}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                        {userIsUniversalHeadAdmin(session) && (
                            <>
                                <CommandSeparator />
                                <CommandGroup>
                                    <div className="p-2">
                                        <CreateCustomerDialog />
                                    </div>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

